import './SubtopicList.scss';
import "@jbtronics/bs-treeview/styles/bs-treeview.css";

import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { BS5Theme, BSTreeView, BSTreeViewNode, FAIconTheme } from '@jbtronics/bs-treeview';
import { AppContext } from 'components/AppContext/AppContext';
import { LogLevel } from 'models/LogLevel';
import { TopicModel } from 'models/TopicModel';
import { Button, Col, Form, Row } from 'react-bootstrap';

interface SubtopicListProps {
  onIdentifyKeywords: (subtopics: TopicModel[]) => void,
  isLoading: boolean,
  setIsLoading: (isLoading: boolean) => void,
  subtopics: TopicModel[],
  activeTab: string;
  activeStep: number;
}

const thisStepNumber = 2;

const SubtopicList: FC<SubtopicListProps> = ({ onIdentifyKeywords, isLoading, setIsLoading, subtopics, activeTab, activeStep }) => {
  const { sendLog } = useContext(AppContext);
  const [selectedSubtopicIds, setSelectedSubtopicIds] = useState<Set<number>>(new Set());
  const [expandedSubtopicIds, setExpandedSubtopicIds] = useState<Set<number>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const handleSubtopicSelected = (topicId: number, checked: boolean) => {
    setSelectedSubtopicIds((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(topicId);
      } else {
        newSet.delete(topicId);
      }
      return newSet;
    });
    sendLog(`The topic id ${topicId} was checked: ${checked}`, LogLevel.info);
  }

  const flattenTopics = (topics: TopicModel[]): Map<number, TopicModel> => {
    const map = new Map<number, TopicModel>();

    const traverse = (topic: TopicModel) => {
      if (!map.has(topic.id)) {
        map.set(topic.id, topic);
        topic.prerequisites.forEach(traverse);
      }
    }

    topics.forEach(traverse);
    return map;
  };

  const getAllTopicIds = (topics: TopicModel[]): number[] => {
    const ids: number[] = [];

    flattenTopics(topics).forEach((topic) => {
      ids.push(topic.id);
    });
    return ids;
  }

  const flattenAndFilterTopics = (topics: TopicModel[]): TopicModel[] => {
    const map = new Map<number, TopicModel>();

    const traverse = (topic: TopicModel) => {
      if (!map.has(topic.id) && selectedSubtopicIds.has(topic.id)) {
        const orphanedTopic = { ...topic, prerequisites: [] };
        map.set(topic.id, orphanedTopic);
      }
      topic.prerequisites.forEach(traverse);
    }

    topics.forEach(traverse);
    return Array.from(map).map(([_, topic]) => topic);
  };

  const handleIdentifyKeywords = async () => {
    setIsLoading(true);
    const filteredSubtopics = flattenAndFilterTopics(subtopics);
    try {
      await onIdentifyKeywords(filteredSubtopics);
    } finally {
      setIsLoading(false);
    }
  }

  // see: https://github.com/jbtronics/bs-treeview
  // see also: https://jbtronics.github.io/bs-treeview/classes/bstreeviewoptions.html
  const treeView = useRef<BSTreeView | null>(null);

  const convertTopicToTreeNode = (topic: TopicModel): BSTreeViewNode => ({
    text: topic.name,
    id: topic.id.toString(),
    selectable: false,
    state: {
      checked: selectedSubtopicIds.has(topic.id),
      disabled: false,
      expanded: expandedSubtopicIds.has(topic.id),
      selected: false,
      _visible: true,
    },
    nodes: topic.prerequisites.map((child) => (convertTopicToTreeNode(child) as BSTreeViewNode)),
  } as BSTreeViewNode);

  const treeData: BSTreeViewNode[] = subtopics.map((topic) => (convertTopicToTreeNode(topic)));

  const onToggleAll = (toggleAll: boolean) => {
    setIsAllSelected(!toggleAll);
    setSelectedSubtopicIds((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (!toggleAll) {
        getAllTopicIds(subtopics).forEach((id) => newSet.add(id));
      } else {
        getAllTopicIds(subtopics).forEach((id) => newSet.delete(id));
      }
      return newSet;
    });
    if (toggleAll) {
      treeView.current?.uncheckAll({ silent: true });
    } else {
      treeView.current?.checkAll({ silent: true });
    }

    sendLog(`All topics selected: ${!isAllSelected}`, LogLevel.info);
  };

  const onExpandAll = (expandAll: boolean) => {
    setIsAllExpanded(!expandAll);
    setExpandedSubtopicIds((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (!expandAll) {
        getAllTopicIds(subtopics).forEach((id) => newSet.add(id));
      } else {
        getAllTopicIds(subtopics).forEach((id) => newSet.delete(id));
      }
      return newSet;
    });
    if (expandAll) {
      treeView.current?.collapseAll({ silent: true });
    } else {
      treeView.current?.expandAll({ silent: true });
    }
    sendLog(`All topics expanded: ${!isAllExpanded}`, LogLevel.info);
  };

  const checkedHandler = (event: Event) => {
    const { node } = (event as CustomEvent).detail;
    handleSubtopicSelected(parseInt(node.id, 10), node.state.checked);
  };

  const expandedHandler = (event: Event) => {
    const { node } = (event as CustomEvent).detail;
    setExpandedSubtopicIds((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (node.state.expanded) {
        newSet.add(parseInt(node.id, 10));
      } else {
        newSet.delete(parseInt(node.id, 10));
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (activeTab !== 'topic-authority' || activeStep !== thisStepNumber) {
      return;
    }
    const treeElement = document.getElementById("treeview");
    requestAnimationFrame(() => {
      if (!treeView.current) {
        treeView.current = new BSTreeView(treeElement!, {
          data: treeData,
          onNodeChecked: checkedHandler,
          onNodeUnchecked: checkedHandler,
          onNodeExpanded: expandedHandler,
          onNodeCollapsed: expandedHandler,
          showCheckbox: true,
          expandIcon: 'bi bi-chevron-right',
          collapseIcon: 'bi bi-chevron-down',
          checkedIcon: 'bi bi-check-square',
          uncheckedIcon: 'bi bi-square',
        }, [BS5Theme, FAIconTheme]);
      }
    });
  }, [activeTab, treeData]);

  return (
    <div className="potential-subtopics" style={{ display: activeStep === thisStepNumber ? 'block' : 'none' }}>
      <Form>
        <h2>Select subtopics to explore keyword opportunities for:</h2>
        <div className='justify-content-left'>
          <ul className='list-group'>
            <li className='list-group-item p-0 m-0' style={{ color: 'black' }}>
              <Row className='p-0 m-0'>
                <Col style={{ padding: 'var(--bs-list-group-item-padding-y) var(--bs-list-group-item-padding-x)', backgroundColor: '#f8f9fa' }}>
                  <Button
                    variant="outline-dark"
                    id='expandAll'
                    onClick={() => { onExpandAll(isAllExpanded); }}
                    disabled={isLoading}
                    style={{ fontWeight: 'bold', display: 'inline-block', padding: '0 0.25em' }}
                    className="expand-button"
                    aria-label="Expand all subtopics"
                    title="Expand all subtopics"
                  >
                    {isAllExpanded ? <i className="bi bi-chevron-down" /> : <i className="bi bi-chevron-right" />}
                  </Button>
                  <Form.Check
                    id='selectAll'
                    onChange={() => { onToggleAll(isAllSelected); }}
                    disabled={isLoading}
                    checked={isAllSelected}
                    className=''
                    style={{ fontWeight: 'bold', display: 'inline-block' }}
                    aria-label="Select all subtopics"
                    label={isAllSelected ? "Deselect All" : "Select All"} />
                </Col>
              </Row>
            </li>
          </ul>

        </div>
        <div id="treeview" className="treeview" />
        <div className="d-flex justify-content-end">
          <Button variant="primary" onClick={handleIdentifyKeywords} disabled={isLoading}>
            Identify Keyword Opportunities
          </Button>
        </div>
      </Form>
    </div>
  );
}
export default SubtopicList;
